import { useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent } from 'react';

interface ISelectedCell<RowId extends string | number> {
  rowId: RowId;
  columnKey: string;
  value: string;
}

export function useSelectableTableCell<RowId extends string | number>() {
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);
  const [selectedCell, setSelectedCell] = useState<ISelectedCell<RowId> | null>(null);

  const handleSelectCell = (rowId: RowId, columnKey: string, value: string) => {
    setSelectedCell({
      rowId,
      columnKey,
      value,
    });
    tableWrapperRef.current?.focus();
  };

  const handleKeyDown = async (e: KeyboardEvent<HTMLDivElement>) => {
    if (!selectedCell) {
      return;
    }

    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
      e.preventDefault();
      await navigator.clipboard.writeText(selectedCell.value);
    }
  };

  const isSelectedCell = (rowId: RowId, columnKey: string) => {
    return selectedCell?.rowId === rowId && selectedCell.columnKey === columnKey;
  };

  const getSelectableCellStyle = (rowId: RowId, columnKey: string): CSSProperties => {
    return {
      cursor: 'pointer',
      outline: isSelectedCell(rowId, columnKey) ? '2px solid var(--mantine-color-blue-6)' : 'none',
      outlineOffset: '-2px',
    };
  };

  return {
    tableWrapperRef,
    selectedCell,
    handleSelectCell,
    handleKeyDown,
    isSelectedCell,
    getSelectableCellStyle,
  };
}
